// frontend/src/components/ProtectedRoute.jsx
import React from "react";
import { Navigate, Outlet } from "react-router-dom";
import PropTypes from "prop-types";
import { useAuth } from "../context/AuthContext";

/**
 * ProtectedRoute — renders child routes only when the user is authenticated.
 * Redirects to /login otherwise.
 *
 * Usage (as a layout route):
 *   <Route element={<ProtectedRoute />}>
 *     <Route path="/codes" element={<CodesPage />} />
 *   </Route>
 *
 * Or with an explicit `children` prop:
 *   <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
 */
function ProtectedRoute({ children }) {
  const { token, loading } = useAuth();

  if (loading) {
    // Avoid flashing /login while the initial /me fetch is in flight
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500 text-sm">Loading…</p>
      </div>
    );
  }

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return children ?? <Outlet />;
}

ProtectedRoute.propTypes = {
  children: PropTypes.node,
};

ProtectedRoute.defaultProps = {
  children: undefined,
};

export default ProtectedRoute;
